import { FC, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Listbox } from '@headlessui/react';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiAppointment from 'hooks/useRoiAppointment';
import { APPOINTMENT_STATUS, COLOR_CODES } from 'utils/constants';
import PeriodComparison from '../PeriodComparison/PeriodComparison';

ChartJS.register(ArcElement, Tooltip, Legend);

const STATUS_OPTIONS = [
  { label: 'Appts Requested', value: undefined }, // it means it's all appts
  { label: 'Appts Accepted', value: APPOINTMENT_STATUS.ACCEPTED.KEY },
  { label: 'Appts Rejected', value: APPOINTMENT_STATUS.CANCELLED.KEY },
];

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  value: number;
  pct: string;
  color: string;
}

interface AppointmentsRequestedProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const UNKNOWN_SERVICE_LABEL = 'Unknown';

const AppointmentsRequested: FC<AppointmentsRequestedProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
    pct: '',
    color: '',
  });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isValidating } = useRoiAppointment({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    status: status.value,
    clinicIds,
  });

  const services = [
    ...((data?.periodData?.totalAppointmentsByService as unknown as {
      serviceName: string;
      total: number;
    }[]) ?? []),
  ].sort((a, b) => {
    const aUnknown = a.serviceName === UNKNOWN_SERVICE_LABEL;
    const bUnknown = b.serviceName === UNKNOWN_SERVICE_LABEL;
    if (aUnknown && !bUnknown) return 1;
    if (!aUnknown && bUnknown) return -1;
    return 0;
  });

  const isEmpty = services.length === 0;

  const chartData = {
    labels: isEmpty ? ['No data'] : services.map((s) => s.serviceName),
    datasets: [
      {
        data: isEmpty ? [1] : services.map((s) => s.total),
        backgroundColor: isEmpty
          ? ['#E8E8E8']
          : services.map((_, i) => COLOR_CODES[i]),
        borderColor: '#FFFFFF',
        borderWidth: isEmpty ? 0 : 1,
        hoverBorderColor: '#FFFFFF',
        hoverBorderWidth: isEmpty ? 0 : 1,
        hoverOffset: 0,
      },
    ],
  };

  const totalAppointments = data?.periodData?.totalAppointments ?? 0;
  const lastTotalAppointments = data?.lastPeriodData?.totalAppointments ?? 0;

  return (
    <div className="px-3.2 py-2.4 shadow-elevation-06-hover bg-white min-h-[44rem] flex flex-col">
      {(isLoading || isValidating) ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <>
          <Listbox value={status} onChange={setStatus}>
            {({ open }) => (
              <div className="relative mb-1.6">
                <Listbox.Button className="flex items-center gap-x-0.8 cursor-pointer outline-none">
                  <span className="text-16 font-bold leading-[2.4rem]">
                    {status.label}
                  </span>
                  <ChevronDownIcon className="w-1.4 h-1.4" />
                </Listbox.Button>
                {open && (
                  <Listbox.Options className="absolute mt-0.5 bg-white rounded-[1rem] shadow-primary z-10 py-1.5 flex flex-col gap-y-1.5 w-max outline-none max-h-[20rem] overflow-y-auto">
                    {STATUS_OPTIONS.map((option) => (
                      <Listbox.Option
                        key={option.label}
                        value={option}
                        className={({ active, selected }) =>
                          `cursor-pointer pl-1.6 pr-2.4 ${
                            active || selected ? 'text-magenta' : ''
                          }`
                        }
                      >
                        {option.label}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                )}
              </div>
            )}
          </Listbox>

          <span className="text-30 font-bold text-magenta">
            {totalAppointments.toLocaleString()}
          </span>

          <div className="mt-0.8">
            <PeriodComparison
              current={totalAppointments}
              last={lastTotalAppointments}
            />
          </div>

          <div className="mt-2.4">
            <div ref={chartContainerRef} className="relative w-full h-[20rem]">
              <Doughnut
                data={chartData}
                options={{
                  cutout: '60%',
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      enabled: false,
                      external: isEmpty
                        ? undefined
                        : ({ chart, tooltip: tip }) => {
                            if (tip.opacity === 0) {
                              setTooltip((prev) =>
                                prev.visible
                                  ? { ...prev, visible: false }
                                  : prev
                              );
                              return;
                            }

                            const dataIndex =
                              tip.dataPoints?.[0]?.dataIndex ?? 0;
                            const value = services[dataIndex]?.total ?? 0;
                            const total = services.reduce(
                              (a, b) => a + b.total,
                              0
                            );
                            const pct =
                              total > 0
                                ? ((value / total) * 100).toFixed(1)
                                : '0.0';
                            const label =
                              services[dataIndex]?.serviceName ?? '';
                            const color = COLOR_CODES[dataIndex] ?? '#B01BAE';

                            const containerRect =
                              chartContainerRef.current?.getBoundingClientRect();
                            const canvasRect =
                              chart.canvas.getBoundingClientRect();

                            const x =
                              canvasRect.left -
                              (containerRect?.left ?? 0) +
                              tip.caretX;
                            const y =
                              canvasRect.top -
                              (containerRect?.top ?? 0) +
                              tip.caretY;

                            setTooltip((prev) => {
                              if (
                                prev.visible &&
                                prev.x === x &&
                                prev.y === y &&
                                prev.label === label &&
                                prev.value === value &&
                                prev.pct === pct &&
                                prev.color === color
                              ) {
                                return prev;
                              }
                              return {
                                visible: true,
                                x,
                                y,
                                label,
                                value,
                                pct,
                                color,
                              };
                            });
                          },
                    },
                  },
                }}
              />

              {tooltip.visible && (
                <div
                  className="absolute z-20 pointer-events-none bg-white text-darkest-grey text-14 leading-[2.1rem] px-1.6 py-1.2 rounded-[1rem] shadow-primary whitespace-nowrap"
                  style={{
                    left: tooltip.x,
                    top: tooltip.y,
                    transform: 'translate(0, -110%)',
                  }}
                >
                  <div className="font-bold mb-0.4">{tooltip.label}</div>
                  <div className="flex items-center gap-x-0.8">
                    <span
                      className="w-0.8 h-0.8 rounded-full shrink-0"
                      style={{ backgroundColor: tooltip.color }}
                    />
                    <span>{tooltip.value.toLocaleString()} appts</span>
                    <span className="text-mid-grey">({tooltip.pct}%)</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2.4 flex flex-wrap gap-x-1.6 gap-y-0.8">
              {services.map((s, i) => (
                <div
                  key={s.serviceName}
                  className="flex items-center gap-x-0.8"
                >
                  <span
                    className="w-0.8 h-0.8 rounded-full shrink-0"
                    style={{ backgroundColor: COLOR_CODES[i] }}
                  />
                  <span className="text-12 text-darkest-grey">
                    {s.serviceName}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AppointmentsRequested;
